/**
 * 选中状态管理 Hook
 * 处理树节点的选中状态
 */

import { useState, useCallback, useEffect } from 'react';
import React from 'react';
import { loopAllChildrenSimple } from '../utils/util';
import { TreeData } from '../iTree';

interface UseSelectStateProps {
    defaultSelectedKeys?: string[];
    selectedKeys?: string[];
    multiple?: boolean;
    selectable?: boolean;
    cancelUnSelect?: boolean;
    onSelect?: (selectedKeys: string[], info: {
        event: string;
        eventObject?: React.MouseEvent | React.KeyboardEvent;
        node: React.ReactElement;
        selected: boolean;
        selectedNodes: React.ReactElement[];
    }) => void;
    children?: React.ReactNode;
    syncCheckedAndSelectedStatus?: boolean;
    checkedKeys?: string[];
    checkStrictly?: boolean;
    controlled?: boolean;
    cacheTreeNodes?: React.ReactElement[];
    treeData?: TreeData[];
}

interface UseSelectStateReturn {
    selectedKeys: string[];
    focusKey: string;
    handleSelect: (treeNode: React.ReactElement, event: React.MouseEvent | React.KeyboardEvent) => void;
    setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    setFocusKey: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * 选中状态管理 Hook
 */
export function useSelectState(props: UseSelectStateProps): UseSelectStateReturn {
    const {
        selectedKeys: controlledSelectedKeys,
        multiple = false,
        selectable = true,
        cancelUnSelect = false,
        onSelect,
        children,
        syncCheckedAndSelectedStatus = false,
        checkedKeys = [],
        checkStrictly = false,
        controlled,
    } = props;

    // 状态管理
    const [selectedKeys, setSelectedKeys] = useState<string[]>(() => {
        return getDefaultSelectedKeys(props);
    });
    const [focusKey, setFocusKey] = useState<string>('');

    // 同步受控的 selectedKeys
    useEffect(() => {
        if (controlledSelectedKeys !== undefined) {
            const keys = getKeysWithMultiple(controlledSelectedKeys, multiple);
            setSelectedKeys(keys);
        }
    }, [controlledSelectedKeys, multiple]);

    // 同步勾选和选中状态
    useEffect(() => {
        if (syncCheckedAndSelectedStatus && !checkStrictly && multiple && checkedKeys) {
            setSelectedKeys(checkedKeys);
        }
    }, [syncCheckedAndSelectedStatus, checkStrictly, multiple, checkedKeys]);

    /**
     * 获取默认选中的keys
     */
    function getDefaultSelectedKeys(props: UseSelectStateProps): string[] {
        const { defaultSelectedKeys, selectedKeys, multiple } = props;

        if (controlled && selectedKeys) {
            return getKeysWithMultiple(selectedKeys, multiple);
        }

        if (defaultSelectedKeys) {
            return getKeysWithMultiple(defaultSelectedKeys, multiple);
        }

        return [];
    }

    /**
     * 根据是否多选处理keys
     */
    function getKeysWithMultiple(keys: string[], multiple?: boolean): string[] {
        if (multiple) {
            return [...keys];
        }
        if (keys.length) {
            return [keys[0]];
        }
        return [];
    }

    /**
     * 处理节点选中
     */
    const handleSelect = useCallback((treeNode: React.ReactElement, event: React.MouseEvent | React.KeyboardEvent) => {
        if (!selectable) return;

        const eventKey = treeNode.props.eventKey || treeNode.key;
        const newSelectedKeys = [...selectedKeys];
        const index = newSelectedKeys.indexOf(eventKey);
        let selected = false;

        // cancelUnSelect为true时第二次点击时不取消选中
        if (cancelUnSelect) {
            if (index === -1) {
                selected = true;
                if (!multiple) {
                    newSelectedKeys.length = 0;
                }
                newSelectedKeys.push(eventKey);
            }
        } else {
            if (index !== -1) {
                selected = false;
                newSelectedKeys.splice(index, 1);
            } else {
                selected = true;
                if (!multiple) {
                    newSelectedKeys.length = 0;
                }
                newSelectedKeys.push(eventKey);
            }
        }

        // 获取选中的节点
        const selectedNodes: React.ReactElement[] = [];
        const treeNodes = children || treeNode.props.cacheTreeNodes;
        if (newSelectedKeys.length && treeNodes) {
            loopAllChildrenSimple(treeNodes as React.ReactElement[], (item: React.ReactElement) => {
                if (newSelectedKeys.indexOf(item.key as string) !== -1) {
                    selectedNodes.push(item);
                }
            });
        }

        const newSt = {
            event: 'select',
            eventObject: event,
            node: treeNode,
            selected,
            selectedNodes,
        };

        if (!controlled) {
            setSelectedKeys(newSelectedKeys);
        }

        onSelect?.(newSelectedKeys, newSt);
    }, [selectedKeys, selectable, cancelUnSelect, multiple, onSelect, children]);

    return {
        selectedKeys,
        focusKey,
        handleSelect,
        setSelectedKeys,
        setFocusKey
    };
}

// 辅助函数已移动到 util.ts